import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import './Auth.css';

// mode: 'signin' | 'signup' | 'forgot' | 'reset'
// 'reset' is set by App.js when Supabase's onAuthStateChange fires a
// PASSWORD_RECOVERY event (user landed here via the email link).
const Auth = ({ initialMode = 'signin', onAuthComplete, onNavigate }) => {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const resetMessages = () => {
    setError('');
    setNotice('');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    onAuthComplete(data.session);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    resetMessages();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    setLoading(false);
    if (signUpError) {
      setError(signUpError.message);
      return;
    }
    if (!data.session) {
      setNotice(`We sent a confirmation link to ${email}. Confirm your email, then sign in.`);
      setMode('signin');
      return;
    }
    onAuthComplete(data.session);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/',
    });
    setLoading(false);
    if (resetError) {
      setError(resetError.message);
      return;
    }
    setNotice(`If an account exists for ${email}, a password reset link has been sent.`);
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    resetMessages();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setNotice('Password updated. You can now use it to sign in.');
    setMode('signin');
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <button type="button" className="auth-logo" onClick={() => onNavigate && onNavigate('landing')}>✈️ AspireAI</button>

        {mode === 'signin' && (
          <>
            <h1 className="auth-title">Welcome back</h1>
            <form onSubmit={handleSignIn} className="auth-form">
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              {error && <div className="auth-error">{error}</div>}
              {notice && <div className="auth-notice">{notice}</div>}
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
            <div className="auth-links">
              <button type="button" onClick={() => { resetMessages(); setMode('forgot'); }}>Forgot password?</button>
              <button type="button" onClick={() => { resetMessages(); setMode('signup'); }}>Create an account</button>
            </div>
          </>
        )}

        {mode === 'signup' && (
          <>
            <h1 className="auth-title">Create your account</h1>
            <form onSubmit={handleSignUp} className="auth-form">
              <input type="text" placeholder="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <input type="password" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              {error && <div className="auth-error">{error}</div>}
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Creating account…' : 'Sign Up'}
              </button>
            </form>
            <div className="auth-links">
              <button type="button" onClick={() => { resetMessages(); setMode('signin'); }}>Already have an account? Sign in</button>
            </div>
          </>
        )}

        {mode === 'forgot' && (
          <>
            <h1 className="auth-title">Reset your password</h1>
            <form onSubmit={handleForgotPassword} className="auth-form">
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              {error && <div className="auth-error">{error}</div>}
              {notice && <div className="auth-notice">{notice}</div>}
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>
            <div className="auth-links">
              <button type="button" onClick={() => { resetMessages(); setMode('signin'); }}>Back to sign in</button>
            </div>
          </>
        )}

        {mode === 'reset' && (
          <>
            <h1 className="auth-title">Set a new password</h1>
            <form onSubmit={handleSetNewPassword} className="auth-form">
              <input type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              {error && <div className="auth-error">{error}</div>}
              {notice && <div className="auth-notice">{notice}</div>}
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Saving…' : 'Save New Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Auth;
